import { useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { maintenanceService } from 'services/api';
import toast from 'features/toast';
import { isBottomFromTechnicianCoordinatorBoolean } from 'components/helpers/IsBottomFromTechnicianCoordinatorBoolean';
import { IsSuperAdminGuard } from 'components/helpers/IsSuperAdmin';
import { MAINTENANCE_TYPE_OPTIONS } from 'constants/maintenanceTypes';

interface MaintenanceTypeCellProps {
  entityId: number;
  maintenanceType?: { value: number; display_name: string } | null;
}

export const MaintenanceTypeCell = ({ entityId, maintenanceType }: MaintenanceTypeCellProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<number | ''>(maintenanceType?.value ?? '');
  const [typeCode, setTypeCode] = useState(maintenanceType?.display_name || '');

  useEffect(() => {
    setTypeCode(maintenanceType?.display_name || '');
    setSelectedType(maintenanceType?.value ?? '');
  }, [maintenanceType?.display_name, maintenanceType?.value]);

  const isDisabledEdit = !isBottomFromTechnicianCoordinatorBoolean() && !IsSuperAdminGuard();
  const label = typeCode
    ? t(`entities.maintenance.maintenance_type_options.${typeCode}`)
    : t('actions.insert');

  const handleClickOpen = () => !isDisabledEdit && setOpen(true);
  const handleClose = () => {
    setSelectedType(maintenanceType?.value ?? '');
    setOpen(false);
  };

  const handleSubmit = () => {
    const option = MAINTENANCE_TYPE_OPTIONS.find((item) => item.value === selectedType);
    maintenanceService
      .update(entityId, { maintenance_type: selectedType === '' ? null : selectedType })
      .then(() => {
        setTypeCode(option?.code || '');
        toast.success(t('toasts.updated-cell'));
        setOpen(false);
      });
  };

  return (
    <>
      <ButtonGroup variant="outlined" size="small">
        {!isDisabledEdit ? (
          <Button onClick={handleClickOpen}>{label}</Button>
        ) : (
          <span>{typeCode ? label : '—'}</span>
        )}
      </ButtonGroup>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('entities.maintenance.maintenance_type')}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="dense"
            label={t('entities.maintenance.maintenance_type')}
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value === '' ? '' : Number(e.target.value))}>
            <MenuItem value="">{t('entities.maintenance.empty_option')}</MenuItem>
            {MAINTENANCE_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(`entities.maintenance.maintenance_type_options.${option.code}`)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('actions.cancel')}</Button>
          <Button onClick={handleSubmit}>{t('actions.confirm')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MaintenanceTypeCell;
